import { cloneDeep } from 'lodash-es';
import { useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { RendererType } from '../data-models/field-definition';
import { field2ToFormField, FieldEntity, FormOptions } from '../data-models/field2.data-model';
import { ICompanySearchMeta, ISelectMeta } from '../data-models/field3.data-model';
import {
  IFinanceRoundDataModel,
  isNonEquityRoundType,
  RoundInstrumentType,
} from '../data-models/finance-round.data-model';
import { ITransactionDataModel } from '../data-models/transaction.data-model';
import { SearchType } from '../services/queries/MaggieCompanyQueries';
import { customFieldsByEntityAtom } from '../services/state/AdminPanel/CustomFieldsStateJ';
import { formatISODateOnly } from '../util/formatters/DateFormatters';
import { getRoundLabel, isPrivateEquityClient } from '../util/preference-utils';
import { createFormField, IFormField } from '../view-models/form.view-model';
import {
  amountField,
  booleanField,
  dateField,
  percentField,
  sharesField,
  simpleTextField,
  useRoundIdField,
} from './CommonFields';
import { useAtomValue } from 'jotai';

export const SECURITY_TYPE_OPTIONS = [
  { displayName: 'Priced Equity', value: RoundInstrumentType.equity as string },
  { displayName: 'SAFE', value: RoundInstrumentType.safe as string },
  { displayName: 'Convertible Note', value: RoundInstrumentType.note as string },
];

function securityTypeField() {
  return createFormField<ISelectMeta<string>>({
    key: 'type',
    label: 'Security Type',
    renderer: RendererType.singleSelect,
    rendererMeta: {
      values: SECURITY_TYPE_OPTIONS,
    },
  });
}

// On type switch, clear values that no longer apply — explicit nulls (the API preserves
// omitted keys). Skips mount so editing an existing round never wipes loaded values.
function useClearInapplicableTermsOnTypeSwitch(type: string | null | undefined) {
  const { setValue } = useFormContext<Partial<IFinanceRoundDataModel>>();
  const prevTypeRef = useRef(type);

  useEffect(() => {
    const prevType = prevTypeRef.current;
    if (prevType === type) {
      return;
    }
    prevTypeRef.current = type;

    const wasNonEquity = isNonEquityRoundType(prevType);
    const nowNonEquity = isNonEquityRoundType(type);
    if (wasNonEquity && !nowNonEquity) {
      setValue('manualValuationCap', null);
      setValue('manualInterestRate', null);
      setValue('manualDiscountRate', null);
    } else if (!wasNonEquity && nowNonEquity) {
      setValue('postMoneyValuation', null);
    }
  }, [type, setValue]);
}

export function useInvestmentFields() {
  const customFields = useAtomValue(customFieldsByEntityAtom);
  const roundCustomFields = (customFields.get(FieldEntity.round) ?? []).reduce((acc, field) => {
    if (!field.systemMeta?.formRefs?.includes(FormOptions.addingRound)) {
      return acc;
    }
    acc.push(field2ToFormField(field));
    return acc;
  }, [] as IFormField<unknown>[]);

  const roundLabel = getRoundLabel();

  const { control } = useFormContext<Partial<IFinanceRoundDataModel>>();
  const type = useWatch({ name: 'type', control });
  useClearInapplicableTermsOnTypeSwitch(type);

  // FD-4241: SAFE/note rounds capture note terms instead of the priced-equity valuation.
  // Tracker-derived types outside the picker (Warrant, Token) keep the equity layout.
  const termsFields: IFormField<unknown>[] = isNonEquityRoundType(type)
    ? [
        amountField({ key: 'manualValuationCap', label: 'Valuation Cap' }),
        percentField({ key: 'manualInterestRate', label: 'Interest Rate' }),
        percentField({ key: 'manualDiscountRate', label: 'Discount %' }),
      ]
    : [amountField({ key: 'postMoneyValuation', label: 'Post Money Valuation' })];

  const fields: IFormField<unknown>[] = [
    simpleTextField({
      key: 'name',
      label: `${roundLabel} Name`,
      required: true,
    }),

    dateField({
      key: 'roundDate',
      label: `${roundLabel} Date`,
      required: true,
    }),
    booleanField({ key: 'initialQSBSQualification', label: 'Qualify for QSBS', required: true }),
    securityTypeField(),
    sharesField({ key: 'fullyDilutedShares', label: `Company's Post-Money Fully Diluted Shares` }),
    ...termsFields,
    amountField({ key: 'raiseAmount', label: `Raise Amount` }),
    ...investorFields,
    ...roundCustomFields,
  ];

  const stageField = useRoundIdField({ key: 'roundId', required: false });

  if (!isPrivateEquityClient()) {
    fields.splice(1, 0, stageField);
  }

  return fields;
}

export const investorFields = [
  createFormField<ICompanySearchMeta>({
    key: '_viewModel.leadInvestors',
    label: 'Lead Investor(s)',
    renderer: RendererType.companySearch,
    rendererMeta: {
      addLabel: (inputText) => inputText,
      createOnSelection: false,
      multiSelect: true,
      showAdd: true,
      searchType: SearchType.Investor,
    },
  }),
  createFormField<ICompanySearchMeta>({
    key: '_viewModel.coInvestors',
    label: 'Co-Investors',
    renderer: RendererType.companySearch,
    rendererMeta: {
      addLabel: (inputText) => inputText,
      createOnSelection: false,
      multiSelect: true,
      showAdd: true,
      searchType: SearchType.Investor,
    },
  }),
];

export function getInitialTransactionValues(transaction: ITransactionDataModel) {
  return cloneDeep({
    ...transaction,
  });
}

export function createInitialInvestmentValues(
  transaction?: ITransactionDataModel
): Partial<IFinanceRoundDataModel> {
  return {
    companyId: transaction?.companyId ?? -1,
    id: transaction?.investmentRoundId ?? undefined,
    name: null,
    roundDate: formatISODateOnly(new Date()),
    roundId: null,
    fullyDilutedShares: null,
    postMoneyValuation: null,
    raiseAmount: null,
  };
}
